import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Spinner } from "react-bootstrap";
import { getSecuritiesByIssuerAccountId } from "../../service/api/SecurityService";
import { generateSecurityReport } from "../../service/api/SecurityReportService";
import { toast } from "sonner";
import { useLoader } from "../../service/Hooks/useLoader";
import { azureBlobService } from "../../service/AzureBlobService";
import { formatAmountInThousands } from "../../service/utils/formatters";

// Composant pour l'en-tête du modal
const ModalHeader = ({ title, onClose }) => (
  <div className="modal-header">
    <h5 className="modal-title">{title}</h5>
    <button type="button" className="btn-close" onClick={onClose}></button>
  </div>
);

// Composant pour le corps du modal avec chargement, erreur ou table
const ModalBody = ({ loading, error, securities, handleGeneratePropertyCertificate, formatDate }) => {
  if (loading) {
    return (
      <div className="text-center">
        <Spinner animation="border" variant="primary" />
        <p>Chargement des titres...</p>
      </div>
    );
  }

  if (error) {
    return <p className="text-danger">Erreur: {error}</p>;
  }

  if (securities.length === 0) {
    return <p className="text text-muted text-center">Aucun titre trouvé pour ce compte.</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-striped align-middle">
        <thead>
          <tr>
            <th>Détenteur</th>
            <th>Titre</th>
            <th>Prix initial</th>
            <th>Prix actuel</th>
            <th>Coupon (%)</th>
            <th>Quantité</th>
            <th>Marché</th>
            <th>Date création</th>
            <th>Date Dernière modification</th>
            {securities && securities.some(s => s.targetMarket !== 'Privé' && s.targetMarket !== 'Private') && (
            <th style={{ textAlign: "center" }}>Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {securities.map((security) => (
            <tr key={security.id}>
              <td>{security.accountFirstname} {security.issuerAccountName}</td>
              <td>{security.name}</td>
              <td className="text-end text-nowrap">
                <span className="counter-amount-badge">
                  {formatAmountInThousands(security.issuePrice || 0)} 
                </span>
              </td>
              <td className="text-end text-nowrap">
                <span className="counter-amount-badge">
                  {formatAmountInThousands(security.lastPrice || 0)}
                </span>
              </td>
              <td className="text-center"><strong className="text-success">{security.couponRate}%</strong></td>
              <td className="text-center">{security.totalUnitsIssue}</td>
              <td>{security.targetMarket}</td>
              <td className="text-center">{formatDate(security.creationDate)}</td>
              <td className="text-center">{formatDate(security.lastUpdateDate)}</td>
              {(security.targetMarket !== 'Privé' && security.targetMarket !== 'Private') && (
              <td className="text-center">
                <button 
                  className="btn btn-primary btn-sm" 
                  onClick={() => handleGeneratePropertyCertificate(security.id)}
                  title="Générer l'attestation de propriété"
                >
                  <i className="fa-solid fa-file-pdf me-1"></i>
                  Générer Attestation de propriete
                </button>
              </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Composant pour la pagination
const Pagination = ({ page, totalPages, setPage }) => (
  <nav>
    <ul className="pagination justify-content-center">
      <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
        <button className="page-link" onClick={() => setPage((prev) => Math.max(prev - 1, 1))}>
          <i className="fa-solid fa-angle-left"></i>
        </button>
      </li>
      {Array.from({ length: totalPages }, (_, i) => (
        <li key={i} className={`page-item ${page === i + 1 ? "active" : ""}`}>
          <button className="page-link" onClick={() => setPage(i + 1)}>
            {i + 1}
          </button>
        </li>
      ))}
      <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
        <button className="page-link" onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}>
          <i className="fa-solid fa-angle-right"></i>
        </button>
      </li>
    </ul>
  </nav>
);

// Composant principal ListeSecurities
const ListeSecurities = ({ issuerAccountId, domainId, onClose }) => {
  const [securities, setSecurities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const pageSize = 5;

  const { showLoader, hideLoader, setProgress } = useLoader();

  useEffect(() => {
    if (!issuerAccountId || !domainId) return;
    const fetchSecurities = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getSecuritiesByIssuerAccountId(issuerAccountId, domainId, page, pageSize);
        setSecurities(data.items || data || []);
        setTotalPages(data.totalPages || Math.ceil((data.totalItems || 0) / pageSize) || 1);
      } catch (err) {
        setError(err.message || "Impossible de récupérer les titres.");
      } finally {
        setLoading(false);
      }
    };
    fetchSecurities();
  }, [issuerAccountId, domainId, page]);

  // Nouveau gestionnaire avec SecurityId et sauvegarde Azure
  const handleGeneratePropertyCertificate = async (securityId) => {
    if (!securities || securities.length === 0) {
      toast.warning("Aucun titre trouvé pour ce compte. Génération impossible.");
      return;
    }

    showLoader({ message: "Génération de l'Attestation en cours...", indeterminate: true });
    setIsGeneratingReport(true);

    try {
      // Appel de l'API avec SecurityId
      const response = await generateSecurityReport(issuerAccountId, securityId, domainId);

      // Créer le blob pour le téléchargement
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(pdfBlob);
      
      // Récupérer le nom du fichier
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `attestation-propriete-${securityId}-${Date.now()}.pdf`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }

      // Téléchargement immédiat
      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileURL);

      // Sauvegarde dans Azure Blob Storage
      try {
        // Créer un nouveau blob pour l'upload (le premier est consommé)
        const uploadBlob = new Blob([response.data], { type: 'application/pdf' });
        const blobName = `security-reports/${fileName}`;
        
        await azureBlobService.saveToBlobStorage(
          uploadBlob, 
          blobName, 
          'documents' // ou le nom de votre container
        );
        
        // console.log('Fichier sauvegardé dans Azure Blob Storage');
      } catch (azureError) {
        toast.warn('Erreur lors de la sauvegarde Azure, mais le téléchargement a réussi:', azureError);
      }

      toast.success("Attestation générée, envoyée par email et sauvegardée !");
      
    } catch (error) {
      console.error('Erreur détaillée:', error);
      toast.error(error.message || "Erreur lors de la génération de l'attestation de propriété.");
    } finally {
      setIsGeneratingReport(false);
      hideLoader();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  if (!issuerAccountId || !domainId) return null;

  return ReactDOM.createPortal(
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)", display: "block" }}>
      <div className="modal-dialog modal-dialog-centered modal-custom">
        <div className="modal-content">
          <ModalHeader title="Liste des Titres" onClose={onClose} />
          <div className="modal-body">
            <ModalBody 
              loading={loading} 
              error={error} 
              securities={securities} 
              handleGeneratePropertyCertificate={handleGeneratePropertyCertificate} 
              formatDate={formatDate} 
            />
            {!loading && !error && securities.length > 0 && (
              <Pagination page={page} totalPages={totalPages} setPage={setPage} />
            )}
          </div>
        </div>
      </div>
      {isGeneratingReport && (
        <div className="text-center my-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Génération du rapport en cours...
        </div>
      )}
    </div>,
    document.body
  );
};

export default ListeSecurities;